import {
  collection,
  getCountFromServer,
  getDocs,
  limit,
  orderBy,
  query,
  where,
  type QueryConstraint,
} from 'firebase/firestore';
import { firestore } from '../firebase';
import {
  RepairStatus,
  ServiceProvider,
  ServiceVerificationStatus,
  UserStatus,
} from '../types/firestore';
import {
  getActiveRepairStatuses,
  isTerminalRepairRequestStatus,
  REPAIR_STATUS_ORDER,
} from '../utils/repairRequestWorkflow';
import { isSuggestionActive } from '../utils/suggestionArchive';
import {
  isDiscoverableServiceProvider,
  isOperationalServiceProvider,
} from '../utils/providerEligibility';
import { resolveServiceVerificationStatus } from '../utils/serviceVerification';
import { getServiceProviderProfilePath } from '../utils/serviceProviderProfilePath';
import { resolveServiceFulfillmentCapabilities } from '../shared/fulfillment';

export type AdminCommandCenterSection =
  | 'queues'
  | 'requestStats'
  | 'providerStats'
  | 'realProviders'
  | 'dataRisks';

export interface AdminMetric {
  value: number | null;
  label: string;
  description: string;
  partial?: boolean;
}

export interface AdminQueues {
  pendingVerifications: AdminMetric;
  pendingSpecializations: AdminMetric;
  pendingEquipmentTypes: AdminMetric;
  activeSuggestions: AdminMetric;
}

export interface AdminRequestStats {
  total: AdminMetric;
  active: AdminMetric;
  terminal: AdminMetric;
  byStatus: Record<RepairStatus, AdminMetric>;
  problematic: AdminMetric;
}

export interface AdminProviderStats {
  total: AdminMetric;
  real: AdminMetric;
  test: AdminMetric;
  visible: AdminMetric;
  operational: AdminMetric;
  pending: AdminMetric;
  changesRequested: AdminMetric;
  profileIncomplete: AdminMetric;
}

export interface AdminRealProviderPreview {
  uid: string;
  serviceName: string;
  city: string | null;
  verificationStatus: ServiceVerificationStatus | 'unknown';
  isVerified: boolean;
  isTestData: boolean;
  isVisible: boolean;
  isOperational: boolean;
  completeness: {
    hasLocation: boolean;
    hasProfileImage: boolean;
    hasBusinessHours: boolean;
    hasServiceFulfillment: boolean;
    hasSpecializations: boolean;
  };
  activeRequestsLabel: 'Niedostepne w V1';
  publicProfilePath: string | null;
  verificationPath: string | null;
}

export interface AdminDataRisks {
  suspendedAccounts: AdminMetric;
  approvedWithoutLocation: AdminMetric;
  approvedWithoutProfileImage: AdminMetric;
  approvedWithoutBusinessHours: AdminMetric;
  approvedWithoutServiceFulfillment: AdminMetric;
  providerUserInconsistencies: AdminMetric;
}

export interface AdminModuleShortcut {
  key: string;
  label: string;
  description: string;
  to: string;
  count: number | null;
  countLabel: string;
}

export interface AdminCommandCenterReadModel {
  queues: AdminQueues;
  requestStats: AdminRequestStats;
  providerStats: AdminProviderStats;
  realProvidersPreview: AdminRealProviderPreview[];
  dataRisks: AdminDataRisks;
  moduleShortcuts: AdminModuleShortcut[];
  sectionErrors: Partial<Record<AdminCommandCenterSection, string>>;
  isPartial: boolean;
  readAt: Date;
  readLimits: {
    providerPreviewLimit: number;
    dataRiskProviderLimit: number;
    activeSuggestionLimit: number;
  };
}

const PROVIDER_PREVIEW_LIMIT = 80;
const DATA_RISK_PROVIDER_LIMIT = 250;
const ACTIVE_SUGGESTION_LIMIT = 120;

const metric = (
  value: number | null,
  label: string,
  description: string,
  partial = false
): AdminMetric => ({
  value,
  label,
  description,
  partial,
});

const unavailableMetric = (label: string, description: string): AdminMetric =>
  metric(null, label, description, true);

const countCollection = async (
  collectionName: string,
  ...constraints: QueryConstraint[]
): Promise<number> => {
  const snapshot = await getCountFromServer(
    query(collection(firestore, collectionName), ...constraints)
  );
  return snapshot.data().count;
};

const getCountOrThrow = (
  collectionName: string,
  constraints: QueryConstraint[] = []
): Promise<number> => countCollection(collectionName, ...constraints);

export const isRealAdminProvider = (
  provider: Partial<ServiceProvider> | null | undefined
): boolean => provider?.isTestData !== true;

export const isTestAdminProvider = (
  provider: Partial<ServiceProvider> | null | undefined
): boolean => provider?.isTestData === true;

export const isVisibleAdminProvider = (
  provider: Partial<ServiceProvider> | null | undefined
): boolean => isDiscoverableServiceProvider(provider);

export const isOperationalAdminProvider = (
  provider: Partial<ServiceProvider> | null | undefined
): boolean => isOperationalServiceProvider(provider);

const hasValidProviderLocation = (value: unknown): boolean => {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Record<string, unknown>;
  const latitude = [candidate.latitude, candidate.lat, candidate._latitude].find(
    (item) => typeof item === 'number' && Number.isFinite(item)
  ) as number | undefined;
  const longitude = [candidate.longitude, candidate.lng, candidate.lon, candidate._longitude].find(
    (item) => typeof item === 'number' && Number.isFinite(item)
  ) as number | undefined;

  if (latitude === undefined || longitude === undefined) return false;
  return latitude >= -90 && latitude <= 90 && longitude >= -180 && longitude <= 180;
};

const hasActiveExplicitFulfillment = (provider: Partial<ServiceProvider>): boolean => {
  if (!provider.serviceFulfillment || typeof provider.serviceFulfillment !== 'object') return false;
  return resolveServiceFulfillmentCapabilities(provider).enabledModes.length > 0;
};

export const getProviderCompleteness = (provider: Partial<ServiceProvider>) => ({
  hasLocation: hasValidProviderLocation(provider.location),
  hasProfileImage: Boolean(provider.profileImage),
  hasBusinessHours: Boolean(provider.businessHours),
  hasServiceFulfillment: hasActiveExplicitFulfillment(provider),
  hasSpecializations:
    Array.isArray(provider.specializations) &&
    provider.specializations.some((item) => typeof item === 'string' && item.trim().length > 0),
});

export type AdminProviderRiskFlag =
  | 'missingLocation'
  | 'missingProfileImage'
  | 'missingBusinessHours'
  | 'missingServiceFulfillment'
  | 'missingSpecializations';

export const getProviderRiskFlags = (
  provider: Partial<ServiceProvider>
): AdminProviderRiskFlag[] => {
  const completeness = getProviderCompleteness(provider);
  return [
    completeness.hasLocation ? null : 'missingLocation',
    completeness.hasProfileImage ? null : 'missingProfileImage',
    completeness.hasBusinessHours ? null : 'missingBusinessHours',
    completeness.hasServiceFulfillment ? null : 'missingServiceFulfillment',
    completeness.hasSpecializations ? null : 'missingSpecializations',
  ].filter(Boolean) as AdminProviderRiskFlag[];
};

export const getAdminRequestBucket = (
  status: RepairStatus | string | null | undefined
): 'active' | 'terminal' | 'problematic' => {
  if (!status || !Object.values(RepairStatus).includes(status as RepairStatus)) {
    return 'problematic';
  }
  return isTerminalRepairRequestStatus(status as RepairStatus) ? 'terminal' : 'active';
};

const emptyQueues = (): AdminQueues => ({
  pendingVerifications: unavailableMetric(
    'Weryfikacja',
    "serviceProviders.verificationStatus == 'pending_approval'"
  ),
  pendingSpecializations: unavailableMetric('Specjalizacje', "specializations.status == 'pending'"),
  pendingEquipmentTypes: unavailableMetric(
    'Typy sprzetu',
    "equipmentTypeSuggestions.status == 'pending'"
  ),
  activeSuggestions: unavailableMetric(
    'Sugestie',
    "suggestions.status == 'new' po odfiltrowaniu aktywnego archiwum w ograniczonym odczycie"
  ),
});

const emptyRequestStats = (): AdminRequestStats => ({
  total: unavailableMetric('Zgloszenia lacznie', 'Liczba dokumentow repairRequests'),
  active: unavailableMetric('Aktywne', 'Statusy nieterminalne wedlug repairRequestWorkflow'),
  terminal: unavailableMetric(
    'Zakonczone/terminalne',
    'Statusy terminalne wedlug repairRequestWorkflow'
  ),
  problematic: unavailableMetric('Nieznane statusy', 'Status spoza aktualnego RepairStatus'),
  byStatus: REPAIR_STATUS_ORDER.reduce(
    (acc, status) => ({
      ...acc,
      [status]: unavailableMetric(status, `repairRequests.status == '${status}'`),
    }),
    {} as Record<RepairStatus, AdminMetric>
  ),
});

const emptyProviderStats = (): AdminProviderStats => ({
  total: unavailableMetric('Serwisy lacznie', 'Liczba dokumentow serviceProviders'),
  real: unavailableMetric('Realne', 'serviceProviders.isTestData !== true'),
  test: unavailableMetric('Testowe', 'serviceProviders.isTestData == true'),
  visible: unavailableMetric('Widoczne publicznie', 'approved + isVerified == true'),
  operational: unavailableMetric('Operacyjne', 'Widoczne publicznie + nietestowe'),
  pending: unavailableMetric('Weryfikacja', "verificationStatus == 'pending_approval'"),
  changesRequested: unavailableMetric('Do poprawek', "verificationStatus == 'changes_requested'"),
  profileIncomplete: unavailableMetric(
    'Profil niekompletny',
    "verificationStatus == 'profile_incomplete'"
  ),
});

const emptyDataRisks = (): AdminDataRisks => ({
  suspendedAccounts: unavailableMetric('Konta suspended', "users.status == 'suspended'"),
  approvedWithoutLocation: unavailableMetric(
    'Bez lokalizacji',
    'Widoczne profile w ograniczonym skanie bez location'
  ),
  approvedWithoutProfileImage: unavailableMetric(
    'Bez zdjecia',
    'Widoczne profile w ograniczonym skanie bez profileImage'
  ),
  approvedWithoutBusinessHours: unavailableMetric(
    'Bez godzin',
    'Widoczne profile w ograniczonym skanie bez businessHours'
  ),
  approvedWithoutServiceFulfillment: unavailableMetric(
    'Bez metod realizacji',
    'Widoczne profile w ograniczonym skanie bez serviceFulfillment'
  ),
  providerUserInconsistencies: unavailableMetric(
    'Niespojnosc kont/profili',
    'Wymaga laczonego read contractu users + serviceProviders; niedostepne w V1'
  ),
});

const loadQueues = async (): Promise<AdminQueues> => {
  const [verifications, specializations, equipmentTypes, suggestionsSnapshot] = await Promise.all([
    getCountOrThrow('serviceProviders', [where('verificationStatus', '==', 'pending_approval')]),
    getCountOrThrow('specializations', [where('status', '==', 'pending')]),
    getCountOrThrow('equipmentTypeSuggestions', [where('status', '==', 'pending')]),
    getDocs(
      query(
        collection(firestore, 'suggestions'),
        where('status', '==', 'new'),
        limit(ACTIVE_SUGGESTION_LIMIT)
      )
    ),
  ]);

  const activeSuggestions = suggestionsSnapshot.docs.filter((docSnap) =>
    isSuggestionActive(docSnap.data().isArchived)
  ).length;

  return {
    pendingVerifications: metric(
      verifications,
      'Weryfikacja',
      "serviceProviders.verificationStatus == 'pending_approval'"
    ),
    pendingSpecializations: metric(
      specializations,
      'Specjalizacje',
      "specializations.status == 'pending'"
    ),
    pendingEquipmentTypes: metric(
      equipmentTypes,
      'Typy sprzetu',
      "equipmentTypeSuggestions.status == 'pending'"
    ),
    activeSuggestions: metric(
      activeSuggestions,
      'Sugestie',
      `Aktywne suggestions.status == 'new' w limicie ${ACTIVE_SUGGESTION_LIMIT}`,
      suggestionsSnapshot.size >= ACTIVE_SUGGESTION_LIMIT
    ),
  };
};

const loadRequestStats = async (): Promise<AdminRequestStats> => {
  const statusCounts = await Promise.all(
    REPAIR_STATUS_ORDER.map(async (status) => ({
      status,
      count: await getCountOrThrow('repairRequests', [where('status', '==', status)]),
    }))
  );
  const total = await getCountOrThrow('repairRequests');
  const byStatus = statusCounts.reduce(
    (acc, { status, count }) => ({
      ...acc,
      [status]: metric(count, status, `repairRequests.status == '${status}'`),
    }),
    {} as Record<RepairStatus, AdminMetric>
  );
  const activeStatuses = new Set(getActiveRepairStatuses());
  const active = statusCounts
    .filter(({ status }) => activeStatuses.has(status))
    .reduce((sum, { count }) => sum + count, 0);
  const terminal = statusCounts
    .filter(({ status }) => isTerminalRepairRequestStatus(status))
    .reduce((sum, { count }) => sum + count, 0);
  const known = statusCounts.reduce((sum, { count }) => sum + count, 0);
  const problematic = Math.max(total - known, 0);

  return {
    total: metric(total, 'Zgloszenia lacznie', 'Liczba dokumentow repairRequests'),
    active: metric(active, 'Aktywne', 'Suma statusow nieterminalnych wedlug repairRequestWorkflow'),
    terminal: metric(
      terminal,
      'Zakonczone/terminalne',
      'Suma statusow terminalnych wedlug repairRequestWorkflow'
    ),
    byStatus,
    problematic: metric(
      problematic,
      'Nieznane statusy',
      'repairRequests.status spoza aktualnej macierzy'
    ),
  };
};

const loadProviderStats = async (): Promise<AdminProviderStats> => {
  const [total, test, visible, visibleTest, pending, changesRequested, profileIncomplete] =
    await Promise.all([
      getCountOrThrow('serviceProviders'),
      getCountOrThrow('serviceProviders', [where('isTestData', '==', true)]),
      getCountOrThrow('serviceProviders', [
        where('verificationStatus', '==', 'approved'),
        where('isVerified', '==', true),
      ]),
      getCountOrThrow('serviceProviders', [
        where('verificationStatus', '==', 'approved'),
        where('isVerified', '==', true),
        where('isTestData', '==', true),
      ]),
      getCountOrThrow('serviceProviders', [where('verificationStatus', '==', 'pending_approval')]),
      getCountOrThrow('serviceProviders', [where('verificationStatus', '==', 'changes_requested')]),
      getCountOrThrow('serviceProviders', [
        where('verificationStatus', '==', 'profile_incomplete'),
      ]),
    ]);

  return {
    total: metric(total, 'Serwisy lacznie', 'Liczba dokumentow serviceProviders'),
    real: metric(Math.max(total - test, 0), 'Realne', 'serviceProviders.isTestData !== true'),
    test: metric(test, 'Testowe', 'serviceProviders.isTestData == true'),
    visible: metric(
      visible,
      'Widoczne publicznie',
      "verificationStatus == 'approved' && isVerified == true"
    ),
    operational: metric(
      Math.max(visible - visibleTest, 0),
      'Operacyjne',
      'approved + isVerified == true + isTestData !== true'
    ),
    pending: metric(pending, 'Weryfikacja', "verificationStatus == 'pending_approval'"),
    changesRequested: metric(
      changesRequested,
      'Do poprawek',
      "verificationStatus == 'changes_requested'"
    ),
    profileIncomplete: metric(
      profileIncomplete,
      'Profil niekompletny',
      "verificationStatus == 'profile_incomplete'"
    ),
  };
};

const providerDocToPreview = (
  uid: string,
  data: Record<string, unknown>
): AdminRealProviderPreview => {
  const provider = { uid, ...data } as ServiceProvider;
  const status = resolveServiceVerificationStatus(provider);
  const completeness = getProviderCompleteness(provider);
  return {
    uid,
    serviceName: String(provider.serviceName || '').trim() || 'Brak nazwy',
    city: String(provider.address?.city || '').trim() || null,
    verificationStatus: status || 'unknown',
    isVerified: provider.isVerified === true,
    isTestData: provider.isTestData === true,
    isVisible: isVisibleAdminProvider(provider),
    isOperational: isOperationalAdminProvider(provider),
    completeness,
    activeRequestsLabel: 'Niedostepne w V1',
    publicProfilePath: isVisibleAdminProvider(provider) ? getServiceProviderProfilePath(uid) : null,
    verificationPath: status === 'pending_approval' ? '/admin/verifications' : null,
  };
};

const loadRealProvidersPreview = async (): Promise<AdminRealProviderPreview[]> => {
  const snapshot = await getDocs(
    query(
      collection(firestore, 'serviceProviders'),
      orderBy('updatedAt', 'desc'),
      limit(PROVIDER_PREVIEW_LIMIT)
    )
  );
  return snapshot.docs
    .map((docSnap) => providerDocToPreview(docSnap.id, docSnap.data()))
    .filter((provider) => !provider.isTestData)
    .slice(0, 12);
};

const loadDataRisks = async (): Promise<AdminDataRisks> => {
  const [suspendedAccounts, visibleProvidersSnapshot] = await Promise.all([
    getCountOrThrow('users', [where('status', '==', UserStatus.SUSPENDED)]),
    getDocs(
      query(
        collection(firestore, 'serviceProviders'),
        where('verificationStatus', '==', 'approved'),
        where('isVerified', '==', true),
        limit(DATA_RISK_PROVIDER_LIMIT)
      )
    ),
  ]);

  const visibleProviders = visibleProvidersSnapshot.docs.map((docSnap) => ({
    uid: docSnap.id,
    ...(docSnap.data() as Partial<ServiceProvider>),
  }));
  const partial = visibleProvidersSnapshot.size >= DATA_RISK_PROVIDER_LIMIT;

  const countMissing = (predicate: (provider: Partial<ServiceProvider>) => boolean) =>
    visibleProviders.filter((provider) => isRealAdminProvider(provider) && predicate(provider))
      .length;

  return {
    suspendedAccounts: metric(suspendedAccounts, 'Konta suspended', "users.status == 'suspended'"),
    approvedWithoutLocation: metric(
      countMissing((provider) => !provider.location),
      'Bez lokalizacji',
      `Widoczne realne profile bez location w limicie ${DATA_RISK_PROVIDER_LIMIT}`,
      partial
    ),
    approvedWithoutProfileImage: metric(
      countMissing((provider) => !provider.profileImage),
      'Bez zdjecia',
      `Widoczne realne profile bez profileImage w limicie ${DATA_RISK_PROVIDER_LIMIT}`,
      partial
    ),
    approvedWithoutBusinessHours: metric(
      countMissing((provider) => !provider.businessHours),
      'Bez godzin',
      `Widoczne realne profile bez businessHours w limicie ${DATA_RISK_PROVIDER_LIMIT}`,
      partial
    ),
    approvedWithoutServiceFulfillment: metric(
      countMissing((provider) => !provider.serviceFulfillment),
      'Bez metod realizacji',
      `Widoczne realne profile bez serviceFulfillment w limicie ${DATA_RISK_PROVIDER_LIMIT}`,
      partial
    ),
    providerUserInconsistencies: unavailableMetric(
      'Niespojnosc kont/profili',
      'Laczone users/serviceProviders bezpiecznie poza zakresem V1'
    ),
  };
};

const buildShortcuts = (
  queues: AdminQueues,
  providerStats: AdminProviderStats
): AdminModuleShortcut[] => [
  {
    key: 'verifications',
    label: 'Weryfikacje',
    description: 'Decyzje o profilach serwisow.',
    to: '/admin/verifications',
    count: queues.pendingVerifications.value,
    countLabel: 'do decyzji',
  },
  {
    key: 'service-providers',
    label: 'Serwisy',
    description: 'Katalog profili realnych i testowych.',
    to: '/admin/service-providers',
    count: providerStats.total.value,
    countLabel: 'profili',
  },
  {
    key: 'accounts',
    label: 'Konta',
    description: 'Monitor kont, ról i ryzyk dostępu.',
    to: '/admin/accounts',
    count: null,
    countLabel: 'monitor',
  },
  {
    key: 'suggestions',
    label: 'Sugestie',
    description: 'Feedback i decyzje produktowe.',
    to: '/admin/suggestions',
    count: queues.activeSuggestions.value,
    countLabel: 'aktywnych',
  },
  {
    key: 'specializations',
    label: 'Specjalizacje',
    description: 'Katalog kompetencji serwisow.',
    to: '/admin/specializations',
    count: queues.pendingSpecializations.value,
    countLabel: 'pending',
  },
  {
    key: 'equipment-types',
    label: 'Typy sprzetu',
    description: 'Katalog urzadzen i sugestie typow.',
    to: '/admin/equipment-types',
    count: queues.pendingEquipmentTypes.value,
    countLabel: 'pending',
  },
  {
    key: 'notifications',
    label: 'Powiadomienia',
    description: 'Admin feed i wejscia z eventow.',
    to: '/admin/notifications',
    count: null,
    countLabel: 'feed',
  },
  {
    key: 'tools',
    label: 'Narzedzia',
    description: 'Dane testowe i operacje techniczne.',
    to: '/admin/tools',
    count: providerStats.test.value,
    countLabel: 'testowych',
  },
];

export const getAdminCommandCenterReadModel = async (): Promise<AdminCommandCenterReadModel> => {
  const sectionErrors: Partial<Record<AdminCommandCenterSection, string>> = {};
  const [
    queuesResult,
    requestStatsResult,
    providerStatsResult,
    realProvidersResult,
    dataRisksResult,
  ] = await Promise.allSettled([
    loadQueues(),
    loadRequestStats(),
    loadProviderStats(),
    loadRealProvidersPreview(),
    loadDataRisks(),
  ]);

  const queues =
    queuesResult.status === 'fulfilled'
      ? queuesResult.value
      : (sectionErrors.queues = 'Nie udalo sie odczytac kolejek moderacji.') && emptyQueues();
  const requestStats =
    requestStatsResult.status === 'fulfilled'
      ? requestStatsResult.value
      : (sectionErrors.requestStats = 'Nie udalo sie odczytac statystyk zgloszen.') &&
        emptyRequestStats();
  const providerStats =
    providerStatsResult.status === 'fulfilled'
      ? providerStatsResult.value
      : (sectionErrors.providerStats = 'Nie udalo sie odczytac statystyk serwisow.') &&
        emptyProviderStats();
  const realProvidersPreview =
    realProvidersResult.status === 'fulfilled'
      ? realProvidersResult.value
      : (sectionErrors.realProviders = 'Nie udalo sie odczytac podgladu realnych serwisow.') && [];
  const dataRisks =
    dataRisksResult.status === 'fulfilled'
      ? dataRisksResult.value
      : (sectionErrors.dataRisks = 'Nie udalo sie odczytac ryzyk danych.') && emptyDataRisks();

  return {
    queues,
    requestStats,
    providerStats,
    realProvidersPreview,
    dataRisks,
    moduleShortcuts: buildShortcuts(queues, providerStats),
    sectionErrors,
    isPartial:
      Object.keys(sectionErrors).length > 0 ||
      Object.values(queues).some((entry) => entry.partial) ||
      Object.values(dataRisks).some((entry) => entry.partial),
    readAt: new Date(),
    readLimits: {
      providerPreviewLimit: PROVIDER_PREVIEW_LIMIT,
      dataRiskProviderLimit: DATA_RISK_PROVIDER_LIMIT,
      activeSuggestionLimit: ACTIVE_SUGGESTION_LIMIT,
    },
  };
};
